import {
  GridHeaderSelectionCheckboxParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import moment, { Moment } from 'moment-timezone'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import BoxAndLinks from 'src/components/BoxAndLinks/BoxAndLinks'
import DatesPickerDropdown from 'src/components/DatesPickerDropdown/DatesPickerDropdown'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import FilterSection from 'src/components/FilterSection/FilterSection'
import FullExpenseReportTable from 'src/components/FullExpenseReportsTable/FullExpenseReportsTable'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useDebounced, useStringSetState } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import { PaginationResponse } from 'src/models/pagination.models'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectIsLeftFlyoutOpen,
  selectIsRightFlyoutOpen,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { setEquality } from 'src/utils/set.utils'
import { css } from 'styled-components'

export interface ExpenseReportsBoxInfiniteScrollProps {
  fetch: (
    filtersBody: ExpenseReportsFiltersBody,
    page?: number,
    firstCall?: boolean,
  ) => void
  pagination?: PaginationResponse
  loading: boolean
  complete: boolean
}
interface ExpenseReportsBoxProps {
  expenseReports?: ExpenseReportModel[]
  expenseReportsInfiniteScrollProps?: ExpenseReportsBoxInfiniteScrollProps // OLD - use infiniteScrollProps for datagrid table
  bulkSelected?: boolean
  selectedExpenseReports?: ExpenseReportModel[]
  handleExpenseReportsCheckboxClick?: (option: ExpenseReportModel) => void
  handleBulkExpenseReportsCheckboxClick?: () => void
  handleReportBulkAction?: () => void
  handleCloseToolbar?: () => void
  userOptions?: UserOption[]
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  allReports?: boolean
  hasApproverPermissions?: boolean
  loading?: boolean
  setSelectedFilters?: (val: ExpenseReportsAnalyticsEventProperties) => void
  context: 'submitter' | 'approver' | 'manager'
}

export interface ExpenseReportsListCheckboxProps {
  canSelectExpenseReport: (transaction: ExpenseReportModel) => boolean
  handleCheckboxClick?: (ids: GridRowSelectionModel) => void
  handleHeaderCheckboxClick?: (
    params: GridHeaderSelectionCheckboxParams,
  ) => void
}

interface ExpenseReportsFilters {
  keyword: string
  startDate?: Moment
  endDate?: Moment
  status: Set<string>
  members: Set<string>
}

export interface ExpenseReportsFiltersBody {
  search?: string
  sinceTimestamp?: string
  untilTimestamp?: string
  statuses?: string[]
  userIds?: string[]
}

export interface ExpenseReportsAnalyticsEventProperties {
  report_submitted_by?: boolean
  report_status?: string[]
}

const ExpenseReportsBox: React.FC<
  React.PropsWithChildren<ExpenseReportsBoxProps>
> = (props): React.ReactElement => {
  const translate = useTranslate()
  const queryParams = useSelector(selectQueryParams)
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const [initialLoad, setInitialLoad] = useState<boolean>(false)

  const initialFilterValues: ExpenseReportsFilters = useMemo(
    () => ({
      keyword: queryParams['search'] || '',
      startDate:
        queryParams['sinceTimestamp'] === ''
          ? undefined
          : queryParams['sinceTimestamp'] === undefined
            ? moment().subtract(30, 'days')
            : moment(queryParams['sinceTimestamp']),
      endDate:
        queryParams['untilTimestamp'] !== undefined
          ? moment(queryParams['untilTimestamp'])
          : moment(),
      status:
        typeof queryParams['statuses'] === 'string'
          ? new Set<string>([queryParams['statuses']])
          : new Set<string>(queryParams['statuses']),
      members:
        typeof queryParams['userIds'] === 'string'
          ? new Set<string>([queryParams['userIds']])
          : new Set<string>(queryParams['userIds']),
    }),
    [],
  )

  const locale = useSelector(selectNullSafeLocaleFromState)

  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)
  const [keywordFilter, setKeywordFilter] = useState(
    initialFilterValues.keyword,
  )
  const [startDateFilter, setStartDateFilter] = useState(
    initialFilterValues.startDate,
  )
  const [endDateFilter, setEndDateFilter] = useState(
    initialFilterValues.endDate,
  )
  const {
    set: statusSet,
    setSet: setStatusSet,
    handleSelection: handleStatusSelection,
  } = useStringSetState(initialFilterValues.status)
  const {
    set: membersSet,
    setSet: setMembersSet,
    handleSelection: handleRecipientSelection,
  } = useStringSetState(initialFilterValues.members)

  const sinceTimestamp =
    startDateFilter?.format('YYYY-MM-DD') ||
    initialFilterValues.startDate?.format('YYYY-MM-DD')
  const untilTimestamp =
    endDateFilter?.format('YYYY-MM-DD') ||
    initialFilterValues.endDate?.format('YYYY-MM-DD')

  const filtersBody: ExpenseReportsFiltersBody = {
    search: keywordFilter !== '' ? keywordFilter : undefined,
    sinceTimestamp: props.context === 'approver' ? sinceTimestamp : undefined,
    untilTimestamp: props.context === 'approver' ? untilTimestamp : undefined,
    statuses: statusSet.size > 0 ? Array.from(statusSet) : undefined,
    userIds: membersSet.size > 0 ? Array.from(membersSet) : undefined,
  }

  const { debouncedF } = useDebounced((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      props.expenseReportsInfiniteScrollProps?.fetch(filtersBody)
    }
  })

  const pushUrlStateDebounce = useCallback(
    _.debounce(() => {
      setPushNewSearchState(true)
    }, 500),
    [],
  )

  // TODO: why does this not include manager?
  const paramString = queryString.stringify({
    ...filtersBody,
  })

  const pushUrlState = (): void => {
    window.history.pushState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  const replaceUrlState = (): void => {
    window.history.replaceState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  // restores query params on flyout close
  useEffect((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      replaceUrlState()
    }
  }, [isRightFlyoutOpen, isLeftFlyoutOpen])

  useEffect((): void => {
    if (pushNewSearchState) {
      pushUrlState()
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  useEffect((): void => {
    if (!initialLoad) {
      debouncedF()
      props.setSelectedFilters &&
        props.setSelectedFilters({
          report_submitted_by: membersSet.size > 0,
          report_status: statusSet.size > 0 ? Array.from(statusSet) : undefined,
        })
    }
    setInitialLoad(false)
  }, [
    keywordFilter,
    startDateFilter,
    endDateFilter,
    statusSet,
    membersSet,
    initialLoad,
  ])

  useEffect((): void => {
    onFiltersResetClick()
  }, [props.context])

  const resetKeywordFilter = (): void => {
    setKeywordFilter(initialFilterValues.keyword)
  }
  const resetStartDateFilter = (): void => {
    setStartDateFilter(initialFilterValues.startDate)
  }
  const resetEndDateFilter = (): void => {
    setEndDateFilter(initialFilterValues.endDate)
  }
  const resetStatusSet = (): void => {
    setStatusSet(initialFilterValues.status)
  }
  const resetMembersSet = (): void => {
    setMembersSet(initialFilterValues.members)
  }

  const onFiltersResetClick = (): void => {
    resetKeywordFilter()
    resetStartDateFilter()
    resetEndDateFilter()
    resetStatusSet()
    resetMembersSet()
    pushUrlStateDebounce()
  }

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (!setEquality(statusSet, initialFilterValues.status)) {
      appliedCount++
    }
    if (!setEquality(membersSet, initialFilterValues.members)) {
      appliedCount++
    }
    return appliedCount
  }

  const handleSetSince = (val: Moment | undefined): void => {
    setStartDateFilter(val)
    pushUrlStateDebounce()
  }

  const handleSetUntil = (val: Moment | undefined): void => {
    setEndDateFilter(val)
    pushUrlStateDebounce()
  }

  const canSelectExpenseReport = (
    expenseReport: ExpenseReportModel,
  ): boolean => {
    return !(props.context === 'approver'
      ? expenseReport.status !== ExpenseReportStatus.Reviewed
      : expenseReport.status !== ExpenseReportStatus.Draft)
  }

  const handleCheckboxClick = (selection: GridRowSelectionModel): void => {
    // this is stupid but hopefully temporary
    const newlySelectedId = [...selection.ids].filter(
      (id: string | number) =>
        !props.selectedExpenseReports?.some(
          (report: ExpenseReportModel) => report.id === id,
        ),
    )?.[0]
    const newlySelected = props.expenseReports?.find(
      (report: ExpenseReportModel) => report.id === newlySelectedId,
    )
    const newlyUnselected = props.selectedExpenseReports?.filter(
      (report: ExpenseReportModel) => !selection.ids.has(report.id),
    )?.[0]
    if (newlySelected !== undefined) {
      props.handleExpenseReportsCheckboxClick?.(newlySelected)
    }
    if (newlyUnselected !== undefined) {
      props.handleExpenseReportsCheckboxClick?.(newlyUnselected)
    }
  }

  const checkboxProps: ExpenseReportsListCheckboxProps | undefined = {
    canSelectExpenseReport: canSelectExpenseReport,
    handleCheckboxClick: handleCheckboxClick,
    handleHeaderCheckboxClick: props.handleBulkExpenseReportsCheckboxClick,
  }

  // show checkboxes for my reports (can delete) or all reports if i have approver permissions (to mark as paid)
  const showCheckbox =
    (props.context === 'approver' && props.hasApproverPermissions) ||
    props.context === 'submitter'

  return (
    <BoxAndLinks>
      <Box
        noPadding
        css={css`
          display: flex;
          justify-content: center;
          flex-direction: column;
        `}
      >
        <FilterSection
          appliedFiltersCount={filtersAppliedCount()}
          topLeftContent={
            <div
              css={css`
                width: 100%;
                display: flex;
                ${getBreakpointDown(breakpointSizes.SM)(css`
                  flex-direction: column;
                `)}
              `}
            >
              {(props.context === 'approver' ||
                props.context === 'manager') && (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    z-index: 3;
                    padding-right: 40px;
                    ${getBreakpointDown(breakpointSizes.SM)(css`
                      margin-bottom: 15px;
                    `)}
                  `}
                >
                  <span
                    css={css`
                      ${getFontCss(
                        20,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {translate('Date Submitted')}
                  </span>
                  <DatesPickerDropdown
                    since={startDateFilter}
                    until={endDateFilter}
                    setSince={(val: Moment | undefined): void => {
                      handleSetSince(val)
                    }}
                    setUntil={(val: Moment | undefined): void => {
                      handleSetUntil(val)
                    }}
                  />
                </div>
              )}
              <div
                css={css`
                  position: relative;
                  flex-grow: 2;
                `}
              >
                <KeywordFilter
                  value={keywordFilter}
                  onChange={(val: string): void => {
                    setKeywordFilter(val)
                    pushUrlStateDebounce()
                  }}
                  showLabel={false}
                  placeholder={translateSingleString(
                    locale,
                    props.context !== 'submitter'
                      ? 'Search by report name, employee, or amount'
                      : 'Search by report name or amount',
                  )}
                />
              </div>
            </div>
          }
          hideButtonChevron
          filtersFlyoutProps={{
            title: translate('Report Filters'),
            onReset: onFiltersResetClick,
            loading: props.expenseReportsInfiniteScrollProps?.loading,
            filters: (
              <>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                  `}
                >
                  <div
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate('Report Status')}
                  </div>
                  {props.context === 'submitter' && (
                    <Checkbox
                      label={translate('Not Submitted')}
                      onClick={(): void => {
                        handleStatusSelection(ExpenseReportStatus.Draft)
                        pushUrlStateDebounce()
                      }}
                      isSelected={
                        statusSet.has(ExpenseReportStatus.Draft) || false
                      }
                    />
                  )}
                  <Checkbox
                    label={translate(
                      props.context !== 'submitter'
                        ? 'Pending Approval'
                        : 'Submitted',
                    )}
                    onClick={(): void => {
                      handleStatusSelection(ExpenseReportStatus.Pending)
                      pushUrlStateDebounce()
                    }}
                    isSelected={
                      statusSet.has(ExpenseReportStatus.Pending) || false
                    }
                  />
                  <Checkbox
                    label={translate('Approved')}
                    onClick={(): void => {
                      handleStatusSelection(ExpenseReportStatus.Reviewed)
                      pushUrlStateDebounce()
                    }}
                    isSelected={
                      statusSet.has(ExpenseReportStatus.Reviewed) || false
                    }
                  />
                  <Checkbox
                    label={translate('Rejected')}
                    onClick={(): void => {
                      handleStatusSelection(ExpenseReportStatus.Rejected)
                      pushUrlStateDebounce()
                    }}
                    isSelected={
                      statusSet.has(ExpenseReportStatus.Rejected) || false
                    }
                  />
                  <Checkbox
                    label={translate('Paid')}
                    onClick={(): void => {
                      handleStatusSelection(ExpenseReportStatus.Paid)
                      pushUrlStateDebounce()
                    }}
                    isSelected={
                      statusSet.has(ExpenseReportStatus.Paid) || false
                    }
                  />
                </div>
                {props.userOptions !== undefined &&
                  props.context !== 'submitter' && (
                    <>
                      <DividerLineDiv
                        css={css`
                          margin: 20px 0px;
                        `}
                      />
                      <div
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        {translate('Report Submitted By')}
                      </div>
                      <UserDropdown
                        css={css`
                          margin-top: 8px;
                        `}
                        isCreatable={false}
                        placeholder={translateSingleString(
                          locale,
                          'All employees',
                        )}
                        chooseOption={(val: string): void => {
                          handleRecipientSelection(val)
                          pushUrlStateDebounce()
                        }}
                        isMulti={true}
                        selectedValues={membersSet}
                        optionGroups={[
                          {
                            options: props.userOptions,
                          },
                        ]}
                        onResetClick={resetMembersSet}
                        infiniteScrollProps={props.userInfiniteScrollProps}
                      />
                    </>
                  )}
              </>
            ),
          }}
          // isAll is true for approvers, false for submitters, undefined for managers
          isAll={
            props.context === 'manager'
              ? undefined
              : props.context === 'approver'
          }
        />
        <div
          css={css`
            overflow: hidden;
            border-bottom-left-radius: ${styleConstants.borderRadius.box};
            border-bottom-right-radius: ${styleConstants.borderRadius.box};
          `}
        >
          <FullExpenseReportTable
            expenseReports={props.expenseReports || []}
            bulkSelected={!!props.bulkSelected}
            selectedExpenseReports={props.selectedExpenseReports || []}
            handleExpenseReportsCheckboxClick={
              props.handleExpenseReportsCheckboxClick
            }
            handleBulkExpenseReportsCheckboxClick={
              props.handleBulkExpenseReportsCheckboxClick
            }
            handleReportBulkAction={props.handleReportBulkAction}
            handleCloseToolbar={props.handleCloseToolbar}
            allReports={!!props.allReports}
            complete={!!props.expenseReportsInfiniteScrollProps?.complete}
            showCheckbox={showCheckbox}
            // for new table
            filters={filtersBody}
            checkboxProps={showCheckbox ? checkboxProps : undefined}
            context={props.context}
          />
          {(props.expenseReportsInfiniteScrollProps?.loading ||
            props.loading) && <LoadingBoxBottom />}
        </div>
      </Box>
    </BoxAndLinks>
  )
}

export default ExpenseReportsBox
